import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import glassTheme, { glassMixins } from '../styles/glassTheme';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 2rem;
  text-align: center;
`;

const ErrorBox = styled.div`
  ${glassTheme.glass.heavy}
  padding: 3rem;
  max-width: 600px;
  border-radius: ${glassTheme.borderRadius.lg};
`;

const ErrorTitle = styled.h1`
  font-size: 2rem;
  color: ${glassTheme.colors.status.error};
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const ErrorDetails = styled.details`
  margin-top: 1rem;
  text-align: left;
  width: 100%;

  summary {
    cursor: pointer;
    color: ${glassTheme.colors.accent.blue};
    margin-bottom: 1rem;
  }

  pre {
    background: rgba(0, 0, 0, 0.5);
    padding: 1rem;
    border-radius: ${glassTheme.borderRadius.md};
    overflow-x: auto;
    color: ${glassTheme.colors.text.muted};
    font-size: 0.9rem;
  }
`;

const RetryButton = styled.button`
  ${glassMixins.button}
  padding: 1rem 2rem;
  background: ${glassTheme.gradients.cyber};

  &:hover {
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }
`;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error Boundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    // Reload the page
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorContainer>
          <ErrorBox>
            <ErrorTitle>出错了</ErrorTitle>
            <ErrorMessage>
              应用程序遇到了一个错误。请尝试刷新页面或联系技术支持。
            </ErrorMessage>
            <RetryButton onClick={this.handleRetry}>
              重新加载
            </RetryButton>
            {this.state.error && (
              <ErrorDetails>
                <summary>查看错误详情</summary>
                <pre>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </ErrorDetails>
            )}
          </ErrorBox>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}
